/*
 BAKER HUGHES COMPANY CONFIDENTIAL

* Unpublished work copyright 2025 Baker Hughes Company.  All rights reserved.
* This computer code is proprietary and highly confidential to Baker Hughes Company
* and/or its affiliate(s). It may not be used, disclosed, modified, transferred,
* or reproduced without prior written consent, and must be returned on demand.
*/
import { useEntity, useRelatedEntities, EntityProvider } from '@backstage/plugin-catalog-react';
import {
  InfoCard,
  Progress,
  EmptyState,
} from '@backstage/core-components';
import { ApiDefinitionCard } from '@backstage/plugin-api-docs';
import { useEffect } from 'react';

// Extend the Window interface to include apiDocOpen
declare global {
  interface Window {
    apiDocOpen?: boolean;
  }
}

export const CustomEntityProvidedApisCard = () => {
  const { entity } = useEntity();
 
  const { entities: providedApis, loading, error } = useRelatedEntities(entity, {
    type: 'providesApi',
  });

  useEffect(() => {
    // This function now finds and removes both elements
    const removeSwaggerElements = () => {
      // Remove the "Authorize" button wrapper
      const authWrapper = document.querySelector('.auth-wrapper');
      if (authWrapper) {
        authWrapper.remove();
      }

      // --- ADD THIS BLOCK TO REMOVE THE "TRY IT OUT" BUTTON ---
      // We select all of them as there can be multiple on the page
      const tryOutWrappers = document.querySelectorAll('.try-out');
      tryOutWrappers.forEach(wrapper => {
        wrapper.remove();
      });
      // --- END OF NEW BLOCK ---
    };

    // The MutationObserver waits for the elements to be added to the page
    const observer = new MutationObserver(() => {
      removeSwaggerElements();
    });

    // Start observing the entire document for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Also run it once immediately, in case the elements are already there
    removeSwaggerElements();

    // Clean up the observer when the component is unmounted
    return () => observer.disconnect();
  }, [loading]);
  useEffect(() => {
    window.apiDocOpen = true;
  }, []);
  if (loading) {
    return (
      <InfoCard title="Provided API">
        <Progress />
      </InfoCard>
    );
  }
 
  if (error) {
    return (
      <InfoCard title="Provided API">
        <EmptyState
          missing="info"
          title="No information to display"
          description={`There was an error while loading the provided API: ${error.message}`}
        />
      </InfoCard>
    );
  }

  if (!providedApis || providedApis.length === 0) {
    return (
      <InfoCard title="Provided API">
        <EmptyState
          missing="data"
          title="No API found"
          description="This entity does not provide any APIs."
        />
      </InfoCard>
    );
  }

  const firstApi = providedApis[0];

  return (
    <EntityProvider entity={firstApi}>
      <ApiDefinitionCard />
    </EntityProvider>
  );
};